import { apiClient } from "@/lib/api/client";

export type SecurityAllowlistEntryDto = {
  id: string;
  tenant_id: string | null;
  cidr: string;
  label: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type SecurityBlocklistEntryDto = {
  id: string;
  tenant_id: string | null;
  ip: string;
  reason: string;
  source: string;
  status: string;
  blocked_at: string;
  updated_at: string;
};

export type SecurityApiKeyDto = {
  id: string;
  tenant_id: string | null;
  name: string;
  key_prefix: string;
  scopes: string[];
  status: string;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
};

export type SecurityAccessLogDto = {
  id: string;
  tenant_id: string | null;
  actor: string;
  action: string;
  ip: string;
  result: string;
  occurred_at: string;
};

export type SecurityOverviewDto = {
  allowlist: SecurityAllowlistEntryDto[];
  blocklist: SecurityBlocklistEntryDto[];
  api_keys: SecurityApiKeyDto[];
  access_logs: SecurityAccessLogDto[];
};

export type SecurityDeviceIngestionKeyDto = {
  id: string;
  tenant_id: string | null;
  device_id: string;
  device_uid: string;
  device_name: string | null;
  key_prefix: string | null;
  name: string | null;
  status: string;
  last_used_at: string | null;
  created_at: string | null;
};

export type SecurityDeviceIngestionKeyPageDto = {
  items: SecurityDeviceIngestionKeyDto[];
  total: number;
  page: number;
  page_size: number;
};

export async function getSecurityOverview(tenantId?: string | null): Promise<SecurityOverviewDto> {
  const { data } = await apiClient.get<SecurityOverviewDto>("/security", {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function createAllowlistEntry(body: {
  tenant_id?: string | null;
  cidr: string;
  label: string;
  status?: string;
}): Promise<SecurityAllowlistEntryDto> {
  const { data } = await apiClient.post<SecurityAllowlistEntryDto>("/security/allowlist", body);
  return data;
}

export async function updateAllowlistEntry(id: string, body: Partial<{ cidr: string; label: string; status: string }>): Promise<SecurityAllowlistEntryDto> {
  const { data } = await apiClient.put<SecurityAllowlistEntryDto>(`/security/allowlist/${id}`, body);
  return data;
}

export async function deleteAllowlistEntry(id: string): Promise<void> {
  await apiClient.delete(`/security/allowlist/${id}`);
}

export async function createBlocklistEntry(body: {
  tenant_id?: string | null;
  ip: string;
  reason: string;
  source?: string;
  status?: string;
}): Promise<SecurityBlocklistEntryDto> {
  const { data } = await apiClient.post<SecurityBlocklistEntryDto>("/security/blocklist", body);
  return data;
}

export async function updateBlocklistEntry(id: string, body: Partial<{ ip: string; reason: string; source: string; status: string }>): Promise<SecurityBlocklistEntryDto> {
  const { data } = await apiClient.put<SecurityBlocklistEntryDto>(`/security/blocklist/${id}`, body);
  return data;
}

export async function deleteBlocklistEntry(id: string): Promise<void> {
  await apiClient.delete(`/security/blocklist/${id}`);
}

export async function createSecurityApiKey(body: {
  tenant_id?: string | null;
  name: string;
  scopes: string[];
}): Promise<SecurityApiKeyDto> {
  const { data } = await apiClient.post<SecurityApiKeyDto>("/security/api-keys", body);
  return data;
}

export async function updateSecurityApiKey(id: string, body: Partial<{ name: string; scopes: string[]; status: string; last_used_at: string | null }>): Promise<SecurityApiKeyDto> {
  const { data } = await apiClient.put<SecurityApiKeyDto>(`/security/api-keys/${id}`, body);
  return data;
}

export async function deleteSecurityApiKey(id: string): Promise<void> {
  await apiClient.delete(`/security/api-keys/${id}`);
}

export async function getDeviceIngestionKeys(params: {
  search?: string;
  status_filter?: "" | "active" | "revoked";
  page?: number;
  page_size?: number;
}): Promise<SecurityDeviceIngestionKeyPageDto> {
  const { data } = await apiClient.get<SecurityDeviceIngestionKeyPageDto>("/security/device-ingestion-keys", {
    params: {
      search: params.search || undefined,
      status_filter: params.status_filter || undefined,
      page: params.page,
      page_size: params.page_size,
    },
  });
  return data;
}

export async function revokeDeviceIngestionKey(id: string): Promise<SecurityDeviceIngestionKeyDto> {
  const { data } = await apiClient.post<SecurityDeviceIngestionKeyDto>(`/security/device-ingestion-keys/${id}/revoke`);
  return data;
}
